/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ThemeSwitcher from './theme3/ThemeSwitcher';
import Header from './theme3/Header';
import HeroSection from './theme3/HeroSection';
import CategoryGrid from './theme3/CategoryGrid';
import ProductGrid from './theme3/ProductGrid';
import ProductCard from './theme3/ProductCard'; // Added for the sliders
import { Swiper, SwiperSlide } from 'swiper/react'; // Added for sliders
import { Autoplay, Navigation, Pagination } from 'swiper/modules'; // Added for sliders
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useStoreBootstrap } from "./theme1/hooks/useStoreBootstrap";
import { useDisplayData } from "./theme1/hooks/useDisplayData";
import { useSmartCart } from './hooks/useSmartCart';
import {
  getCurrentCustomer,
  createTenantOrder,
  getCustomerOrders,
  updateTenantOrder,
  cancelCustomerOrder,
  addStoreReview,
  getStoreReviews,
  getDeliveryByCity,
  initiatePayment
} from './utils/storeApi';
import { buildImageUrl, n } from "./theme1/utils/theme1Helpers";

// Modals
import CartSidebar from "./theme3/CartSidebar";
import ProductModal from "./theme1/components/ProductModal";
import AuthModal from './components/AuthModal';
import OrdersModal from "./theme3/OrdersModal";
import EditOrderModal from "./theme3/EditOrderModal";
import OrderDetailsPage from "./theme3/OrderDetailsPage";
import ReviewModal from "./theme3/ReviewModal";
import WhatsAppButton from "./theme2/components/WhatsAppButton";
import Swal from "sweetalert2";

const TikTokIcon = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M33.5 4c.9 7.3 6.1 11.6 13.5 11.9v7.5c-4.1.2-7.7-1-11.1-3.1v14.7c0 7.3-6 13.3-13.3 13.3S9.3 42.3 9.3 35s6-13.3 13.3-13.3c.9 0 1.8.1 2.6.3v7.7c-.8-.3-1.6-.5-2.6-.5-3.1 0-5.6 2.5-5.6 5.6s2.5 5.6 5.6 5.6 5.6-2.5 5.6-5.6V4h7.3z" />
  </svg>
);

const themes = {
  original: { primary: '#5B4DFF', text: 'text-[#5B4DFF]', bg: 'bg-[#5B4DFF]', hover: 'hover:bg-[#483cdb]', primaryDark: '#483cdb', background: '#F9FAFB' },
  royalBlue: { primary: '#2563eb', text: 'text-[#2563eb]', bg: 'bg-[#2563eb]', hover: 'hover:bg-[#1d4ed8]', primaryDark: '#1d4ed8', background: '#F9FAFB' },
  forestGreen: { primary: '#059669', text: 'text-[#059669]', bg: 'bg-[#059669]', hover: 'hover:bg-[#047857]', primaryDark: '#047857', background: '#F9FAFB' },
  burntOrange: { primary: '#ea580c', text: 'text-[#ea580c]', bg: 'bg-[#ea580c]', hover: 'hover:bg-[#c2410c]', primaryDark: '#c2410c', background: '#F9FAFB' },
  deepRose: { primary: '#e11d48', text: 'text-[#e11d48]', bg: 'bg-[#e11d48]', hover: 'hover:bg-[#be123c]', primaryDark: '#be123c', background: '#F9FAFB' },
};

const Theme3Store = ({ tenantId, tenant }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [isSticky, setIsSticky] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('original');
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false); // تم الإضافة
  const [showAllReviews, setShowAllReviews] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pendingCartItem, setPendingCartItem] = useState(null);
  const readSavedCustomer = useCallback(() => {
    try {
      const saved = localStorage.getItem("storeCustomer");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }, []);

  const [customer, setCustomer] = useState(() => readSavedCustomer());

  const [customerOrders, setCustomerOrders] = useState([]);
  const [storeReviews, setStoreReviews] = useState([]); // تم الإضافة
  const [newReview, setNewReview] = useState({ rating: 5, description: "" }); // تم الإضافة
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [deliveryForm, setDeliveryForm] = useState({
    name: "", email: "", phone: "", address: "", cityId: localStorage.getItem("selectedCityId") || ""
  });
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const asText = (value) => (value === null || value === undefined ? "" : String(value).trim());
  const asNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  const {
    loading, storeData, apiProducts, apiCategories, apiCoupons, apiBanners, apiTags, cities
  } = useStoreBootstrap(tenantId);

  const {
    displayProducts, displayCategories, displayCoupons
  } = useDisplayData({
    apiProducts, apiCategories, apiCoupons, apiTags
  });

  const {
    cart, addItem, updateQuantity, removeItem, clearCart, refreshCart,
  } = useSmartCart(tenantId);

  const resolveThemeKey = (name = "") => {
    const key = name.trim();
    return themes[key] ? key : null;
  };

  useEffect(() => {
    const key =
      resolveThemeKey(tenant?.colorName) ||
      resolveThemeKey(storeData?.colorName);
    if (key) setCurrentTheme(key);
  }, [tenant?.colorName, storeData?.colorName]);

  useEffect(() => {
    const syncCustomer = () => setCustomer(readSavedCustomer());
    const handleVisibility = () => {
      if (!document.hidden) syncCustomer();
    };

    window.addEventListener("storage", syncCustomer);
    window.addEventListener("focus", syncCustomer);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("storage", syncCustomer);
      window.removeEventListener("focus", syncCustomer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [readSavedCustomer]);

  useEffect(() => {
    if (displayProducts?.length) {
      window.__STORE_PRODUCTS__ = displayProducts;
      refreshCart();
    }
  }, [displayProducts, refreshCart]);

  // Handlers
  const applyCouponCode = (rawCode) => {
    setCouponError("");
    if (cart.length === 0) return setCouponError("السلة فارغة");
    const code = (rawCode || couponCode).toUpperCase().trim();
    const coupon = displayCoupons.find(c => c.code === code);
    if (!coupon) return setCouponError("كوبون غير صحيح");
    setAppliedCoupon(coupon);
    setCouponCode("");
  };

  const handleApplyCoupon = (e) => {
    e.preventDefault();
    applyCouponCode();
  };

  const handleCategoryClick = (categoryId, subCategoryId = null) => {
    if (subCategoryId) {
      setSelectedCategory(Number(categoryId));
      setSelectedSubCategory(Number(subCategoryId));
    } else {
      setSelectedCategory(prev => (Number(categoryId) === prev ? 0 : Number(categoryId)));
      setSelectedSubCategory(null);
    }
  };

  const loadCustomerOrders = async () => {
    const cust = getCurrentCustomer();
    if (!cust?.customerId) return;
    const res = await getCustomerOrders(tenantId, cust.customerId);
    if (res.success) setCustomerOrders(res.data);
  };

  // Fetch orders when the user opens "طلباتي" (mirrors Theme1 behavior)
  useEffect(() => {
    if (showOrdersModal) {
      loadCustomerOrders();
    }
  }, [showOrdersModal]);

  const handleOpenProduct = ({ productId, variantId }) => {
    const p = displayProducts.find(x => x.id === productId);
    if (!p) return;
    const v = p.variants.find(x => Number(x.variantId) === Number(variantId));
    setSelectedProduct({ productId, variantId: v?.variantId });
    setShowProductModal(true);
  };

  const handleAddToCart = async (data, qtyToAdd = 1) => {
    if (!localStorage.getItem("storeCustomerId")) {
      setPendingCartItem({ ...data, quantity: qtyToAdd });
      setShowAuthModal(true);
      return;
    }
    const res = await addItem(data, qtyToAdd);
    if (res?.success) { refreshCart(); setShowCartSidebar(true); }
    else { Swal.fire("تنبيه", res?.error || "فشل الإضافة", "warning"); }
  };
  // جلب التقييمات عند تحميل الصفحة
  useEffect(() => {
    const fetchReviews = async () => {
      const res = await getStoreReviews(tenantId);
      if (res.success) {
        setStoreReviews(res.data.filter(r => r.isActive === 1));
      }
    };
    fetchReviews();
  }, [tenantId]);
  const handleCheckoutSubmit = async () => {
    // 1. التحقق من السلة والمحافظة
    if (cart.length === 0) return Swal.fire({ icon: "error", title: "السلة فارغة" });

    const city = cities.find(c => Number(c.cityId) === Number(deliveryForm.cityId));
    if (!city) return Swal.fire({ icon: "error", title: "يرجى اختيار المحافظة" });

    // 2. تجهيز بيانات العميل
    const customer = getCurrentCustomer();
    if (!customer?.customerId) {
      setShowAuthModal(true);
      return;
    }
    const updatedCustomer = {
      ...customer,
      name: asText(deliveryForm.name) || asText(customer?.name),
      phone: asText(deliveryForm.phone) || asText(customer?.phone),
      address: asText(deliveryForm.address) || asText(customer?.address),
      email: asText(deliveryForm.email) || asText(customer?.email),
      cityId: asNumber(deliveryForm.cityId, 0)
    };
    if (!updatedCustomer.name || !updatedCustomer.phone) {
      return Swal.fire({ icon: "error", title: "يرجى إدخال الاسم ورقم الهاتف" });
    }

    localStorage.setItem("storeCustomer", JSON.stringify(updatedCustomer));

    // 3. بناء هيكل الطلب
    const orderObj = {
      orderId: localStorage.getItem("currentOrderId") || "",
      // ... داخل orderObj
       items: cart.map(item => ({
        itemId: asNumber(item.itemId, 0),
        variantId: asNumber(item.variantId, 0),
        itemName: asText(item.name || item.itemName),
        variantName: asText(item.variantName),
        // التأكد من إرسال اللون كـ Hex لكي يظهر في السيرفر والسلة لاحقاً
        colorName: asText(typeof item.color === 'object' ? (item.color.hex || item.color.hexCode) : item.color),
        size: asText(item.size),
        quantity: asNumber(item.quantity, 0),
        price: asNumber(item.price, 0),
        imageUrl: asText(item.imageUrl || item.image),
        stockQty: asNumber(item.stockQty, 0),
        discount: asNumber(item.discount, 0),
        lineTotal: asNumber(item.price, 0) * asNumber(item.quantity, 0),
        costPrice: asNumber(item.costPrice, 0) // ✅ سعر التكلفة
      })),
      totalAmount: asNumber(subtotal, 0),
      netAmount: asNumber(total, 0),
      discountAmount: asNumber(couponDiscount, 0),
      deliveryId: Number(deliveryInfo?.deliveryId || 0)
    };

    const storedCoupon = JSON.parse(localStorage.getItem("storeCoupon") || "null");

    try {
      const result = await createTenantOrder(tenantId, city, orderObj, storedCoupon);
      if (result?.success) {
        localStorage.removeItem("storeCoupon");
        clearCart();
        await refreshCart();

        const fullName = String(updatedCustomer.name || "").trim();
        const [firstName = "", ...lastNameParts] = fullName.split(/\s+/);
        const lastName = lastNameParts.join(" ");
        const paymentOrderId =
          result?.data?.orderId ||
          result?.data?.OrderId ||
          orderObj.orderId;

        const paymentRes = await initiatePayment({
          tenantId,
          orderId: paymentOrderId,
          email: updatedCustomer.email || "",
          phone: updatedCustomer.phone || "",
          firstName,
          lastName
        });

        if (!paymentRes?.success || !paymentRes?.paymentUrl) {
          Swal.fire({
            icon: "warning",
            title: "تم إنشاء الطلب ولكن فشل فتح صفحة الدفع",
            text: paymentRes?.error || "يرجى المحاولة لاحقاً من صفحة الطلبات"
          });
          return;
        }

        const redirectUrl = String(paymentRes.paymentUrl || "").trim();
        window.alert(`PAYMENT URL:\n${redirectUrl || "EMPTY"}`);
        if (!redirectUrl) {
          Swal.fire({
            icon: "warning",
            title: "تعذر فتح رابط الدفع",
            text: "لم يتم استلام رابط صالح من مزود الدفع"
          });
          return;
        }

        // ✅ تم تصحيح الخطأ هنا: استبدال onClose بـ setShowCartSidebar
        setShowCartSidebar(false);
        const openPayment = await Swal.fire({
          icon: "success",
          title: "تم إنشاء الطلب وربط الدفع",
          html: `
            <div style="text-align:right;direction:rtl;font-size:14px">
              <p><b>رقم الطلب:</b> ${paymentOrderId}</p>
              <p style="word-break:break-all"><b>رابط الدفع:</b><br/>${redirectUrl}</p>
              <p>اضغط "فتح صفحة الدفع" للانتقال مباشرة.</p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "فتح صفحة الدفع",
          cancelButtonText: "نسخ الرابط"
        });

        if (openPayment.isConfirmed) {
          window.location.href = redirectUrl;
          return;
        }

        try {
          await navigator.clipboard.writeText(redirectUrl);
          await Swal.fire({
            icon: "info",
            title: "تم نسخ الرابط",
            text: "الصق الرابط في المتصفح لإكمال الدفع"
          });
        } catch {
          await Swal.fire({
            icon: "info",
            title: "انسخ الرابط يدويًا",
            html: `<a href="${redirectUrl}" target="_self" rel="noopener noreferrer">الانتقال إلى صفحة الدفع</a>`
          });
        }
        return;
      } else {
        Swal.fire({ icon: "error", title: result?.error || "فشل إرسال الطلب" });
      }
    } catch (err) {
      console.error("Checkout Error:", err);
      Swal.fire({ icon: "error", title: "خطأ تقني" });
    }
  };

  const handleDeleteOrder = async (order) => {
    const confirm = await Swal.fire({
      title: "تأكيد الحذف",
      text: "هل تريد حذف هذا الطلب نهائياً؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "نعم احذف",
      cancelButtonText: "إلغاء"
    });

    if (!confirm.isConfirmed) return;

    const res = await cancelCustomerOrder(
      tenantId,
      order.orderId,
      order.statusId
    );

    if (res?.success) {
      setCustomerOrders(prev =>
        prev.filter(o => o.orderId !== order.orderId)
      );
      setSelectedOrderDetails(null);
      Swal.fire("تم", "تم حذف الطلب بنجاح", "success");
    } else {
      Swal.fire("خطأ", res?.error || "فشل حذف الطلب", "error");
    }
  };

  const saveEditedOrder = async (order) => {
    if (!order?.items || order.items.length === 0) {
      Swal.fire("خطأ", "الطلب فارغ", "error");
      return;
    }

    const city = cities.find(
      c => Number(c.cityId) === Number(order.cityId)
    );

    if (!city) {
      Swal.fire("خطأ", "المحافظة غير صحيحة", "error");
      return;
    }

    const normalizedOrder = {
      ...order,
      items: order.items.map(d => ({
        itemId: asNumber(d.itemId, 0),
        variantId: asNumber(d.variantId, 0),
        itemName: asText(d.itemName),
        variantName: asText(d.variantName),
        colorName: asText(d.colorName),
        size: asText(d.size),
        quantity: asNumber(d.quantity, 0),
        price: asNumber(d.price, 0),
        imageUrl: asText(d.imageUrl),
        stockQty: asNumber(d.stockQty || 0, 0),
        discount: asNumber(d.discount || 0, 0),
        lineTotal: asNumber(d.quantity, 0) * asNumber(d.price, 0)
      }))
    };

    const res = await updateTenantOrder(tenantId, normalizedOrder, city);

    if (res.success) {
      const savedOrder = res.data?.order || res.data?.data || res.data || normalizedOrder;
      setCustomerOrders(prev =>
        prev.map(o =>
          o.orderId === order.orderId
            ? { ...o, ...savedOrder }
            : o
        )
      );

      setEditingOrder(null);
      Swal.fire("تم", "تم حفظ التعديلات بنجاح", "success");
    } else {
      Swal.fire("خطأ", res.error || "فشل حفظ التعديلات", "error");
    }
  };

  // أضف هذا الـ Effect داخل مكون Theme3Store
  useEffect(() => {
    const savedCustomer = JSON.parse(localStorage.getItem("storeCustomer"));
    if (savedCustomer) {
      setDeliveryForm({
        name: savedCustomer.name || "",
        email: savedCustomer.email || "",
        phone: savedCustomer.phone || "",
        address: savedCustomer.address || "",
        cityId: savedCustomer.cityId || localStorage.getItem("selectedCityId") || ""
      });
    }
  }, [customer]);

  useEffect(() => {
    if (deliveryForm.cityId && tenantId) {
      getDeliveryByCity(tenantId, deliveryForm.cityId).then(res => {
        if (res) setDeliveryInfo(res);
        else setDeliveryInfo(null);
      });
    } else {
      setDeliveryInfo(null);
    }
  }, [deliveryForm.cityId, tenantId]);
  const filteredProducts = useMemo(() => {
    let data = [...(displayProducts || [])];
    if (selectedCategory && selectedCategory !== 0) data = data.filter(p => Number(p.categoryId) === Number(selectedCategory));
    if (selectedSubCategory !== null && selectedSubCategory !== undefined) data = data.filter(p => Number(p.subCategoryId) === Number(selectedSubCategory));
    switch (activeTab) {
      case 'bestseller':
        data = data.filter(p => p.isBestseller);
        break;
      case 'new':
        data = data.filter(p => p.isNewProduct || (p.tags && p.tags.some(t => t.includes("جديد") || t.includes("New"))));
        break;
      default: break;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(p => p.name?.toLowerCase().includes(q));
    }
    return data;
  }, [displayProducts, selectedCategory, selectedSubCategory, activeTab, searchQuery]);

  const subtotal = useMemo(() => cart.reduce((s, i) => s + (i.price * i.quantity), 0), [cart]);
  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;
    const v = n(appliedCoupon.discountValue);
    return appliedCoupon.discountType === 1 ? (subtotal * v) / 100 : Math.min(v, subtotal);
  }, [appliedCoupon, subtotal]);
  const shipping = useMemo(() => {
    if (!deliveryInfo) return 0;
    return Number(deliveryInfo.deliveryCompanyFees || 0);
  }, [deliveryInfo]);

  const total = subtotal - couponDiscount + shipping;

  useEffect(() => {
    const handleScroll = () => {
      const catSec = document.getElementById('categories-section');
      if (catSec) setIsSticky(window.scrollY > catSec.offsetTop - 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center">⏳ جاري التحميل...</div>;
  const theme = themes[currentTheme];

  const socials = storeData?.social ? [
    { key: "facebook", label: "فيسبوك", icon: "fab fa-facebook-f", url: storeData.social.facebook, color: "hover:text-[#1877F2]" },
    { key: "instagram", label: "إنستغرام", icon: "fab fa-instagram", url: storeData.social.instagram, color: "hover:text-[#E4405F]" },
    { key: "snapchat", label: "سناب شات", icon: "fab fa-snapchat-ghost", url: storeData.social.snapchat, color: "hover:text-[#FFFC00]" },
    { key: "tiktok", label: "تيك توك", url: storeData.social.tiktok, renderIcon: () => <TikTokIcon size={24} />, color: "hover:text-white" }
  ].filter(s => s.url) : [];

  return (
    <div
      className="min-h-screen bg-[#F9FAFB] font-sans text-right"
      dir="rtl"
      style={{ width: "100vw", marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
    >
      <ThemeSwitcher themes={themes} setCurrentTheme={setCurrentTheme} />

      <Header
        theme={theme} isSticky={isSticky} storeData={storeData}
        cartCount={cart.reduce((s, i) => s + i.quantity, 0)}
        onCartClick={() => setShowCartSidebar(true)}
        onUserClick={() => setShowAuthModal(true)}
        onOrdersClick={() => {
          loadCustomerOrders();
          setShowOrdersModal(true);
        }}
        searchQuery={searchQuery} onSearchChange={(e) => setSearchQuery(e.target.value)}
        customer={customer} onLogout={() => { localStorage.clear(); window.location.reload(); }}
      />

      <HeroSection
        theme={theme} apiBanners={apiBanners} storeData={storeData}
        onOpenCart={() => setShowCartSidebar(true)}
      />


      <div id="categories-section" className="scroll-mt-20" />
      <CategoryGrid
        categories={displayCategories}
        selectedCategory={selectedCategory}
        selectedSubCategory={selectedSubCategory}
        onCategoryClick={handleCategoryClick}
        theme={theme}
      />

      <div id="products-section" className="scroll-mt-20 px-8">

        <ProductGrid
          theme={theme}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          products={filteredProducts}
          onAddToCart={handleAddToCart}
          onOpenProduct={handleOpenProduct}
        />
      </div>
      {/* قسم آراء العملاء */}
      <section className="py-16 px-8 bg-white">
        <div className="container mx-auto">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800">آراء العملاء</h2>
            <button
              onClick={() => setShowReviewModal(true)}
              className={`px-6 py-2 rounded-full text-white font-bold transition-all ${theme.bg} ${theme.hover}`}
            >
              أضف تقييمك
            </button>
          </div>

          {storeReviews.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(showAllReviews ? storeReviews : storeReviews.slice(0, 3)).map((r, i) => (
                  <div key={i} className="p-6 border border-gray-100 rounded-2xl bg-gray-50 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-500">
                        {r.customerName?.charAt(0)}
                      </div>
                      <strong className="text-gray-700">{r.customerName}</strong>
                    </div>
                    <div className="text-yellow-400 mb-2">
                      {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{r.description}</p>
                  </div>
                ))}
              </div>
              {storeReviews.length > 3 && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="px-6 py-2 border-2 rounded-full font-bold transition-all hover:bg-gray-50"
                    style={{ borderColor: theme.primary, color: theme.primaryDark }}
                  >
                    {showAllReviews ? "إخفاء" : "مشاهدة المزيد"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-gray-500">لا توجد تقييمات بعد. كن أول من يقيمنا!</p>
          )}
        </div>
      </section>
      <footer className="bg-[#0D1117] mt-20 py-16 px-8 text-white text-right">
        <div className="container mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <h2 className="text-2xl font-bold mb-4 text-white">{storeData.storeName || "Madar"}</h2>
            <p className="text-gray-400">{storeData.storeSlogan || "منصة تجارة إلكترونية متكاملة"}</p>
          </div>
          <div>
            <h3 className="font-bold mb-6 text-white">روابط</h3>
            <ul className="text-gray-400 space-y-3 text-sm">
              {storeData.policies?.privacy && (
                <li
                  className="cursor-pointer hover:text-white transition-colors"
                  onClick={() => Swal.fire({ title: 'سياسة الخصوصية', text: storeData.policies.privacy, confirmButtonText: 'حسناً', confirmButtonColor: theme.primary })}
                >
                  سياسة الخصوصية
                </li>
              )}
              {storeData.policies?.delivery && (
                <li
                  className="cursor-pointer hover:text-white transition-colors"
                  onClick={() => Swal.fire({ title: 'سياسة التوصيل', text: storeData.policies.delivery, confirmButtonText: 'حسناً', confirmButtonColor: theme.primary })}
                >
                  سياسة التوصيل
                </li>
              )}
              {storeData.policies?.refund && (
                <li
                  className="cursor-pointer hover:text-white transition-colors"
                  onClick={() => Swal.fire({ title: 'سياسة الاسترجاع', text: storeData.policies.refund, confirmButtonText: 'حسناً', confirmButtonColor: theme.primary })}
                >
                  سياسة الاسترجاع
                </li>
              )}
              {!storeData.policies && (
                <>
                  <li>سياسة الخصوصية</li>
                  <li>الشروط والأحكام</li>
                </>
              )}
            </ul>
          </div>
          <div>
            <h3 className="font-bold mb-6 text-white">الدعم</h3>
            <p className="text-gray-400 mb-4">24/7 دعم فني متميز</p>
          </div>
          <div>
            <h3 className="font-bold mb-6 text-white">تابعنا</h3>
            {socials.length > 0 ? (
              <div className="flex gap-4 items-center">
                {socials.map(s => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`text-gray-400 transition-colors ${s.color}`}
                    title={s.label}
                  >
                    {s.renderIcon ? s.renderIcon() : <i className={`${s.icon} text-2xl`} />}
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">لا توجد روابط حالياً</p>
            )}
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showCartSidebar && (
        <CartSidebar
          tenantId={tenantId}
          theme={theme}
          cities={cities}
          isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
          cart={cart}
          appliedCoupon={appliedCoupon}
          couponDiscount={couponDiscount}
          subtotal={subtotal}
          shipping={shipping}
          total={total}
          displayCoupons={displayCoupons}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          couponError={couponError}
          applyCouponCode={applyCouponCode}
          handleApplyCoupon={handleApplyCoupon}
          handleUpdateQty={updateQuantity}
          handleRemove={removeItem}
          deliveryForm={deliveryForm}
          setDeliveryForm={setDeliveryForm}
          handleCheckoutSubmit={handleCheckoutSubmit}
          setShowAuthModal={setShowAuthModal}
          onClose={() => setShowCartSidebar(false)}
        />
      )}

      {showProductModal && selectedProduct && (
        <ProductModal
          showProductModal={showProductModal}
          setShowProductModal={setShowProductModal}
          selectedProduct={selectedProduct}
          products={displayProducts}
          handleAddToCart={handleAddToCart}
          theme={theme}
          isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
        />
      )}

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          tenantId={tenantId}
          theme={{ primary: theme.primary, secondary: theme.primaryDark }}
          onSuccess={(data) => {
            setCustomer(data);
            loadCustomerOrders();
            setShowAuthModal(false);
            if (pendingCartItem) {
              handleAddToCart(pendingCartItem, pendingCartItem.quantity);
              setPendingCartItem(null);
            }
          }}
        />
      )}

      {showOrdersModal && !selectedOrderDetails && (
        <OrdersModal
          show={showOrdersModal}
          onClose={() => setShowOrdersModal(false)}
          orders={customerOrders}
          openOrderDetails={(order) => {
            setSelectedOrderDetails(order);
            setShowOrdersModal(false);
          }}
          theme={theme}
        />
      )}

      {selectedOrderDetails &&
        <OrderDetailsPage
          order={selectedOrderDetails}
          theme={theme}
          onClose={() => setSelectedOrderDetails(null)}
          onEdit={(order) => {
            setEditingOrder(JSON.parse(JSON.stringify(order)));
            setSelectedOrderDetails(null);
          }}
          onDelete={handleDeleteOrder}
        />
      }

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          products={apiProducts}
          theme={theme}
          onCancel={() => setEditingOrder(null)}
          onSave={saveEditedOrder}
        />
      )}

      {/* Review Modal الربط الصحيح */}
      {showReviewModal && (
        <ReviewModal
          show={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          tenantId={tenantId}
          newReview={newReview}
          setNewReview={setNewReview}
          addStoreReview={addStoreReview}
          getStoreReviews={getStoreReviews}
          setStoreReviews={setStoreReviews}
          theme={theme}
        />
      )}

      <WhatsAppButton storeData={storeData} />

      <style>{`
        html { scroll-behavior: smooth; }
        .scroll-mt-20 { scroll-margin-top: 80px; }
      `}</style>
    </div>
  );
};

export default Theme3Store;
